import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useRealtimeChannel } from '@readsavor/auth';
/**
 * @class CouponProvider
 * @description 优惠券事件的“桥梁”提供者 (Bridge Provider)。
 *
 * @purpose
 * 该组件的核心职责是监听来自 Supabase Realtime 的、关于用户优惠券和优惠券模板的实时变更事件。
 * 它通过监听 `user_resource_coupons` 和 `resource_coupon_templates` 两张表，
 * 解决了因服务器端主动变更（如管理员发放、禁用模板）导致前端数据陈旧的问题。
 */
export const CouponProvider = ({ children }) => {
    const { session } = useAuthStore();
    const queryClient = useQueryClient();
    const userId = session?.user?.id;
    // 1. 设置用户相关的频道 (仅当用户登录时)
    const userCouponsChannelName = userId ? `user-resource-coupons-changes-${userId}` : null;
    const userCouponsChannel = useRealtimeChannel(userCouponsChannelName);
    // 2. 设置全局的、与用户无关的频道 (始终保持订阅)
    const templatesChannel = useRealtimeChannel('resource-coupon-templates-changes');
    // 效果一: 监听用户优惠券的变化
    useEffect(() => {
        if (!userCouponsChannel || !userId)
            return;
        const listener = userCouponsChannel.on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'user_resource_coupons',
            filter: `user_id=eq.${userId}`,
        }, _payload => {
            queryClient.invalidateQueries({ queryKey: ['coupons'] });
        });
        return () => {
            listener.unsubscribe();
        };
    }, [userCouponsChannel, userId, queryClient]);
    // 效果二: 监听优惠券模板的变化 (全局)
    useEffect(() => {
        if (!templatesChannel)
            return;
        const listener = templatesChannel.on('postgres_changes', {
            event: 'UPDATE',
            schema: 'public',
            table: 'resource_coupon_templates',
        }, _payload => {
            queryClient.invalidateQueries({ queryKey: ['coupons'] });
        });
        return () => {
            listener.unsubscribe();
        };
    }, [templatesChannel, queryClient]);
    return _jsx(_Fragment, { children: children });
};
//# sourceMappingURL=CouponProvider.js.map